/**
 * Pricing tiles + Razorpay checkout entry.
 */
import { PRICING, openCheckout } from '../chassis/razorpay.ts';

const TPL = /* html */ `
<style>
:host {
  display: block;
  margin: 40px 0 20px;
  position: relative;
}
.shell {
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  background:
    linear-gradient(180deg, oklch(99% 0.012 80) 0%, oklch(94% 0.022 80) 100%);
  padding: 26px 22px 22px;
  border-radius: 2px;
  position: relative;
}
.shell::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid oklch(60% 0.040 50 / 0.25);
  border-radius: 2px;
  pointer-events: none;
}
.head {
  text-align: center;
  margin-bottom: 22px;
}
.eyebrow {
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--ink-dim, oklch(40% 0.04 50));
}
h3 {
  font-family: var(--display, serif);
  font-size: 28px;
  font-style: italic;
  font-weight: 600;
  margin: 6px 0 4px;
  color: var(--ink, oklch(20% 0.04 50));
}
.tagline {
  font-family: var(--display, serif);
  font-size: 14px;
  color: var(--ink-dim, oklch(40% 0.04 50));
}
.tiers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 12px;
}
.tier {
  background: oklch(98% 0.012 80);
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  padding: 18px 14px;
  border-radius: 2px;
  display: grid;
  gap: 8px;
  cursor: pointer;
  transition: transform 100ms;
  position: relative;
}
.tier:hover { transform: translateY(-2px); }
.tier.recommended { border-color: var(--crimson, #9b1c1c); }
.tier.recommended::after {
  content: 'recommended';
  position: absolute;
  top: -10px;
  left: 12px;
  background: var(--crimson, #9b1c1c);
  color: var(--paper, #f5ead1);
  font-family: var(--mono, monospace);
  font-size: 8.5px;
  padding: 3px 8px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
}
.tier .label {
  font-family: var(--mono, monospace);
  font-size: 10px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: var(--ink-dim, oklch(40% 0.04 50));
}
.tier .price {
  font-family: var(--display, serif);
  font-size: 28px;
  font-style: italic;
  color: var(--crimson, #9b1c1c);
  font-weight: 700;
}
.tier .per {
  font-family: var(--mono, monospace);
  font-size: 9.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint, oklch(50% 0.04 50));
}
.tier .desc {
  font-family: var(--display, serif);
  font-size: 13px;
  color: var(--ink, oklch(20% 0.04 50));
}
.tier button {
  appearance: none;
  font-family: var(--mono, monospace);
  font-size: 10px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 10px;
  background: var(--ink, oklch(20% 0.04 50));
  color: var(--paper, #f5ead1);
  border: none;
  border-radius: 2px;
  cursor: pointer;
  margin-top: 6px;
}
.tier.recommended button { background: var(--crimson, #9b1c1c); }
.footer {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed oklch(60% 0.040 50 / 0.4);
  font-family: var(--mono, monospace);
  font-size: 9.5px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-align: center;
  color: var(--ink-faint, oklch(50% 0.04 50));
}
.footer a {
  color: var(--crimson, #9b1c1c);
  text-decoration: none;
  border-bottom: 1px solid oklch(46% 0.180 25 / 0.3);
}
.footer a:hover { border-bottom-color: var(--crimson, #9b1c1c); }
.success {
  margin-top: 16px;
  padding: 12px;
  background: oklch(94% 0.06 145 / 0.4);
  border-left: 3px solid oklch(50% 0.16 145);
  font-family: var(--display, serif);
  font-size: 14px;
  display: none;
}
.success.show { display: block; }
</style>
<section class="shell">
  <div class="head">
    <div class="eyebrow">Pricing · MMXXVI</div>
    <h3>The book is free. The ink writes itself for ₹999.</h3>
    <div class="tagline">Voice, camera, AI drift watch, multi-device sync — Pro tier ke saath.</div>
  </div>
  <div class="tiers">
    <div class="tier" data-tier="free">
      <div class="label">Free</div>
      <div class="price">₹0</div>
      <div class="per">forever</div>
      <div class="desc">90-day diary, encrypted on-device. No genius tools.</div>
      <button data-go="free">Current plan</button>
    </div>
    <div class="tier recommended" data-tier="pro_yearly">
      <div class="label">Pro · Yearly</div>
      <div class="price">₹999</div>
      <div class="per">per year</div>
      <div class="desc">Voice + camera + drift watch + similar search. 1 device.</div>
      <button data-go="pro_yearly">Buy Pro</button>
    </div>
    <div class="tier" data-tier="family_yearly">
      <div class="label">Family · Yearly</div>
      <div class="price">₹2999</div>
      <div class="per">per year · 4 members</div>
      <div class="desc">Pro for 4. Mom-Dad-Bhai-Bhabhi-You.</div>
      <button data-go="family_yearly">Buy Family</button>
    </div>
    <div class="tier" data-tier="bundle_yearly">
      <div class="label">Empire Bundle</div>
      <div class="price">₹2999</div>
      <div class="per">all 8 apps · 1 year</div>
      <div class="desc">Hisaab + Khazaana + Tankhwah + Sehat + Aramgah + Almari + Dial + Wasiyat.</div>
      <button data-go="bundle_yearly">Buy Bundle</button>
    </div>
  </div>
  <div class="success" id="success"></div>
  <div class="footer">
    Razorpay · <a href="https://taxwalaai.com/refund" target="_blank" rel="noopener">30-day refund</a> · GST e-invoice · <a href="https://taxwalaai.com/privacy" target="_blank" rel="noopener">Privacy</a> · <a href="mailto:support@taxwalaai.com">support@taxwalaai.com</a>
  </div>
</section>
`;

export class HisaabPayCta extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const success = this.#shadow.getElementById('success') as HTMLDivElement;
    this.#shadow.querySelectorAll<HTMLButtonElement>('[data-go]').forEach((b) => {
      b.addEventListener('click', async () => {
        const tier = b.dataset.go;
        if (tier === 'free' || typeof tier !== 'string') return;
        if (tier !== 'pro_yearly' && tier !== 'family_yearly' && tier !== 'bundle_yearly') return;
        try {
          await openCheckout(tier, 'Hisaab', {}, (pid) => {
            success.textContent = `Payment captured · id ${pid}. Email going to wickedrita@gmail.com for activation.`;
            success.classList.add('show');
            localStorage.setItem(`hisaab:tier`, tier);
            localStorage.setItem(`hisaab:payment_id`, pid);
          });
        } catch (e) {
          success.textContent = `Checkout failed · ${e instanceof Error ? e.message : 'unknown'}. Email aditya@taxwalaai.com.`;
          success.classList.add('show');
        }
      });
    });
    void PRICING; // referenced for typecheck
  }
}

customElements.define('hisaab-pay-cta', HisaabPayCta);
